import { IconButton, RoundedFilledButton } from '@/components/buttons/Buttons';
import EmptyTable from '@/components/empty/EmptyTable';
import SearchBarInput from '@/components/form-inputs/SearchBarInput';
import { Table } from '@/components/table/Tables';
import { Column } from 'primereact/column';
import { Divider } from 'primereact/divider';
import { Toolbar } from 'primereact/toolbar';
import type { Nullable } from 'primereact/ts-helpers';
import { classNames } from 'primereact/utils';
import { useState } from 'react';
import { MdFilterAlt, MdToday } from 'react-icons/md';
import style from '../../../views/employee.module.css';
import StatusTag from '../../StatusTag';
import Filters from '../Filters';
import TransactionDetails from './TransactionDetails';
import { X4bEmployeeDetailsDto } from '@/types';
import { copyWith, formatCurrency, GenericResponse, ListResponse } from '@/common/common_utils';
import DateInput from '@/components/inputs/DateInput';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/services/axios.service';
import { ENDPOINTS } from '@/constants/endpoints';
import { DateHelper } from '@/common/DateHelper';
import moment from 'moment';
import { X4bPaymentDto, GetPaymentListRequest } from '@/modules/vehicle/engine/X4bPaymentDto';
import {
	convertPaymentStatus,
	expandUiStatusesForFilter,
	PAYMENT_STATUSES_UI,
	paymentStatusSeverity,
	type PaymentStatusUi,
} from '@/modules/vehicle/engine/PaymentStatus';
import { XpressVehicleType } from '@/modules/vehicle/engine/VehicleType';
import clsx from 'clsx';

type Props = {
	employee: X4bEmployeeDetailsDto;
};

type Transaction = {
	Id: string;
	Date: string;
	Time: string;
	Type: string;
	Status: PaymentStatusUi;
	Amount: number;
};

const FinanceTab = (props: Props) => {

	const emp = props.employee;

	const [globalFilter, setGlobalFilter] = useState('');
	const [date, setDate] = useState<moment.Moment | null>(null);
	const [showTransaction, setShowTransaction] = useState<boolean>(false);
	const [selectedPayment, setSelectedPayment] = useState<X4bPaymentDto | null>(null);
	const [showFilter, setShowFilter] = useState<boolean>(false);
	const [typeFilter, setTypeFilter] = useState<XpressVehicleType[]>([XpressVehicleType.Moto, XpressVehicleType.Sedan, XpressVehicleType.SUV]); // All vehicle types selected by default
	// Kept as string[] so we can reuse the shared Filters sidebar (which is also used
	// from order-history with a different set of values). The list always contains
	// PaymentStatusUi values, which we assert when calling the expand helper.
	const [statusFilter, setStatusFilter] = useState<string[]>([...PAYMENT_STATUSES_UI]); // All statuses selected by default
	const [paginationFilter, setPaginationFilter] = useState({ Skip: 0, Take: 5 });
	const [sort, setSort] = useState({ sortField: '', sortOrder: 0 });

	// Fetch payment transactions for this employee
	const makeListRequest = () => {
		const filters = [];
		if (globalFilter) {
			filters.push({ Name: "Id", Value: globalFilter });
		}
		return {
			Skip: paginationFilter.Skip,
			Take: paginationFilter.Take,
			SortField: sort.sortField,
			SortOrder: sort.sortOrder,
			Filters: filters,
			Date: DateHelper.prepareDateToStore(date?.toDate()),
			// "Pending" expands to ["PENDING","RESERVED"] so reserved rows are returned
			// when the user filters by Pending. See PaymentStatus.ts.
			Status: statusFilter.length < PAYMENT_STATUSES_UI.length ? expandUiStatusesForFilter(statusFilter as PaymentStatusUi[]) : null,
			ProgramIds: null,
			Amount: null,
			UserId: emp.Id, // Filter by this employee
			VehicleTypes: typeFilter.length < 3 ? typeFilter : null // Only send if not all types selected
		} as GetPaymentListRequest;
	};

	const paymentsQ = useQuery({
		queryKey: ['Employee.Payments', emp.Id, paginationFilter, sort, globalFilter, date?.toISOString(), typeFilter, statusFilter],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<ListResponse<X4bPaymentDto>>>(
				ENDPOINTS['/api/payments/GetPaymentList'],
				makeListRequest()
			);
			return result.data.Data;
		},
	});

	// Map payments to Transaction format
	const transactions: Transaction[] = paymentsQ.data?.Items?.map((payment) => ({
		Id: payment.Id?.toString() ?? '',
		Date: payment.Date ? DateHelper.formatDate(new Date(payment.Date)) : '',
		Time: payment.Date ? new Date(payment.Date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '',
		Type: payment.Activity?.VehicleType ? `Ride - ${XpressVehicleType[payment.Activity.VehicleType]}` : 'N/A',
		Status: convertPaymentStatus(payment.Status),
		Amount: payment.Amount ?? 0,
	})) ?? [];

	const statusTemplate = (rowData: Transaction) => (
		<StatusTag
			value={rowData.Status}
			severity={paymentStatusSeverity(rowData.Status)}
		/>
	);

	const valueTemplate = (rowData: Transaction) => (
		<span className="text-title-medium font-normal text-error60">
			-₱{rowData.Amount.toFixed(2)}
		</span>
	);

	const dateTemplate = (rowData: Transaction) => (
		<div>
			<p className="text-title-medium font-normal text-neutral17">
				{rowData.Date}
			</p>
			<p className="text-body-xx-small font-normal text-neutral17">
				{rowData.Time}
			</p>
		</div>
	);

	const startContent = (
		<SearchBarInput
			placeholder="Search here"
			className="w-[360px] h-14"
			value={globalFilter}
			onChange={(e) => setGlobalFilter(e.target.value)}
		/>
	);

	const endContent = (
		<DateInput
			value={date}
			onChange={setDate}
			variant="outlined"
			icon={(<MdToday size={24} className="text-onSurfaceVariant" />)}
			iconCls="h-10 w-10 p-2 rounded-[100px] bg-[#D5D8DB] pointer-events-none top-2 right-1"
			labelBgColor="bg-surfaceContainerLow"
			containerCls="w-[312px]"
			calendarProps={{
				minYear: 2025,
				maxYear: 2074,
			}}
		/>
	);

	const mapPaymentToTransaction = (payment: X4bPaymentDto) => {

		var timeFormatted = payment.Date ? new Date(payment.Date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' }) : '-';
		return {

			id: payment.Id ?? '',
			date: payment.Date ? DateHelper.formatDate(new Date(payment.Date)) : '-',
			time: timeFormatted,
			type: 'Rider Earning',
			//type: payment.Activity?.VehicleType ? `Ride - ${XpressVehicleType[payment.Activity.VehicleType]}` : 'N/A',
			promo: payment.Promo ?? '-',
			promoFunded: payment.PromoFunded ?? 'N/A',
			paymentMethod: payment.PaymentMethod ?? '-',
			amount: payment.Amount ?? 0,
			status: convertPaymentStatus(payment.Status),
			processor: payment.Processor ?? '-',
			reference: payment.ExternalReference ?? '-',
			preauth: timeFormatted,//payment.Preauth ?? '-',
			earningTime: timeFormatted,//payment.EarningTime ?? '-',
			commission: payment.Commission ?? '-',
		}
	};

	const handleFilterClear = () => {
		setTypeFilter([XpressVehicleType.Moto, XpressVehicleType.Sedan, XpressVehicleType.SUV]); // Reset to all types
		setStatusFilter([...PAYMENT_STATUSES_UI]); // Reset to all statuses
	};

	// Calculate active filter count
	const activeFiltersCount = (() => {
		let count = 0;
		if (typeFilter.length < 3 && typeFilter.length > 0) count++; // Vehicle type filter active if not all selected
		if (statusFilter.length < PAYMENT_STATUSES_UI.length) count++; // Status filter active if not all selected
		return count;
	})();

	const emptyTableTemplate = activeFiltersCount > 0
		? EmptyTable({
			title: 'Sorry we couldn\'t find any results',
			src: '/images/svg/no-filter.svg',
		})
		: EmptyTable({
			title: 'We didn\'t find a match',
			description: 'Try searching for something else.',
			src: '/images/svg/no-result.svg',
			descriptionClassName: 'text-primaryContainer',
		});


	return (
		<>
			<div className="space-y-6">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
					<div className="p-4 bg-surfaceContainerLow rounded-2xl">
						<div className="py-2 flex justify-between items-center">
							<p className="text-label-small font-normal">
								Total Spend
							</p>
							<p className="text-label-small font-normal text-neutral40">
								{formatCurrency(emp.TotalSpend)}
							</p>
						</div>
					</div>
					<div className="p-4 bg-surfaceContainerLow rounded-2xl">
						<div className="py-2 flex justify-between items-center">
							<p className="text-label-small font-normal">
								Avg spend per TX
							</p>
							<p className="text-label-small font-normal text-neutral40">
								{formatCurrency(emp.AvgSpendPerTx)}
							</p>
						</div>
					</div>
					<div className="p-4 bg-surfaceContainerLow rounded-2xl">
						<div className="py-2 flex justify-between items-center">
							<p className="text-label-small font-normal">
								Highest TX
							</p>
							<p className="text-label-small font-normal text-neutral40">
								{formatCurrency(emp.HighestTx)}
							</p>
						</div>
					</div>
				</div>

				<div className={style.employeeTransactionContainer}>
					<div className="flex justify-between items-center">
						<h1 className="text-label-large font-semibold text-neutral17">
							Transactions
						</h1>
						<RoundedFilledButton
							id="filter"
							className={clsx(
								"with-icon-button-primary",
								activeFiltersCount !== 0 && "border-2 border-solid border-orange-600"
							)}
							icon={MdFilterAlt}
							iconSize="18"
							label={`Filter${activeFiltersCount === 0 ? '' : `(${activeFiltersCount})`}`}
							onClick={() => setShowFilter(!showFilter)}
							width="120px"
							height='40px'
						/>

					</div>
					<div className="">
						{/* Filters */}
						<Toolbar
							pt={{
								root: {
									className: 'people-toolbar',
								},
							}}
							start={startContent}
							end={endContent}
						/>
						<Divider />
						{/* Transactions Table */}
						<Table
							size="small"
							paginator
							emptyMessage={emptyTableTemplate}
							value={transactions}
							rowsPerPageOptions={[5, 10, 20, 30, 40, 50, 100]}
							rows={paginationFilter.Take}
							first={paginationFilter.Skip}
							lazy
							totalRecords={paymentsQ.data?.TotalRecords}
							loading={paymentsQ.isLoading}
							onPage={(event) => {
								setPaginationFilter(copyWith(paginationFilter, { Skip: event.first, Take: event.rows }));
							}}
							sortField={sort.sortField}
							sortOrder={sort.sortOrder as any}
							onSort={(e) => {
								setSort({
									sortField: e.sortField ?? '',
									sortOrder: e.sortOrder ?? 0,
								});
							}}
							pt={{
								column: {
									headerCell: {
										className: classNames(
											style.employeeTableHeader,
										),
									},
								},
								bodyRow: {
									className: classNames(style.employeeTable),
								},
								paginator: {
									root: {
										className: classNames(
											style.employeeTablePagination,
										),
									},
								},
							}}
							onRowClick={(e) => {
								const rowData = e.data as Transaction;
								const payment = paymentsQ.data?.Items?.find(p => p.Id?.toString() === rowData.Id);
								if (payment) {
									setSelectedPayment(payment);
									setShowTransaction(true);
								}
							}}
							className="md:max-h-[28rem]"
						>
							<Column field="Id" header="TX ID" sortable
								headerClassName="tablet:!w-[80px]"
								bodyClassName="tablet:!w-[80px]"
							/>
							<Column
								field="Date"
								header="Date"
								sortable
								body={dateTemplate}
							/>
							<Column field="Type" header="TX Type" sortable />
							<Column
								field="Status"
								header="Status"
								body={statusTemplate}
								sortable
							/>
							<Column
								field="Amount"
								header="Value"
								body={valueTemplate}
								sortable
							/>
						</Table>
					</div>
				</div>
			</div>
			{selectedPayment && (
				<TransactionDetails
					visible={showTransaction}
					onHide={() => setShowTransaction(false)}
					transaction={mapPaymentToTransaction(selectedPayment)}
				/>
			)}
			<Filters
				typeFilter={typeFilter}
				setTypeFilter={setTypeFilter}
				statusFilter={statusFilter}
				setStatusFilter={setStatusFilter}
				visible={showFilter}
				onHide={() => setShowFilter(false)}
				onFilterClear={handleFilterClear}
				type="finance"
			/>
		</>
	);
};

export default FinanceTab;
